/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import {
    getLocation,
    getUserAgentInfo,
    type Json,
    logWarn,
    type LogTarget,
    LogLevel,
    toJson,
    type Unsubscribe,
    type UserAgentInfo,
} from '@amazon/vinyl-util'
import type { DmlsOptions } from '@amzn/vinyl-amzn'
import { getVinylLogger, getVinylLoggerDmls } from '@amzn/bf-playback/vinyl'
import { redactText } from '@/redactSensitive'
import { onError, type ErrorReport, formatError } from '@/errorHandler'
import { asError } from '@/util/errorUtil'
import { bullfrogVersion } from '@/version'

export const APP_ERROR_EVENT_TYPE = 'appError'

const remoteErrorReportingTarget: LogTarget = { logPrefix: 'remoteErrorReporting' }

const DMLS_WAIT_MS = 5_000

export interface AppErrorLogEvent {
    readonly type: typeof APP_ERROR_EVENT_TYPE
    readonly level: LogLevel
    readonly logPrefix: string
    readonly error: { readonly message: string; readonly name: string; readonly stack: string }
    readonly appVersion: string
    readonly clientInfo: {
        readonly href: string
        readonly userAgentInfo: Json<UserAgentInfo>
        readonly dmlsOptions: Json<DmlsOptions | null>
    }
}

// `error.message` and `clientInfo.dmlsOptions` are the paths of Vinyl's own error events, so log
// queries read the message and filter by `clientInfo.dmlsOptions.customer.customerId` the same way.
export function toAppErrorLogEvent(
    { target, error }: ErrorReport,
    dmlsOptions: DmlsOptions | null
): AppErrorLogEvent {
    const err = asError(error)
    return {
        type: APP_ERROR_EVENT_TYPE,
        level: LogLevel.ERROR,
        logPrefix: target.logPrefix,
        error: { message: redactText(err.message), name: err.name, stack: formatError(err) },
        appVersion: bullfrogVersion.str,
        clientInfo: {
            href: getLocation().href,
            userAgentInfo: toJson(getUserAgentInfo()),
            dmlsOptions: toJson(dmlsOptions),
        },
    }
}

// An error during boot lands before the player auth binding sets the DMLS options; waiting briefly
// lets it still carry the customer.
function settledDmls(): Promise<DmlsOptions | null> {
    const dmls$ = getVinylLoggerDmls()
    if (dmls$.value) return Promise.resolve(dmls$.value)
    return new Promise((resolve) => {
        const finish = (dmls: DmlsOptions | null) => {
            clearTimeout(timer)
            stop()
            resolve(dmls)
        }
        const timer = setTimeout(() => finish(null), DMLS_WAIT_MS)
        const stop = dmls$.onData((dmls) => {
            if (dmls) finish(dmls)
        })
    })
}

/**
 * Sends every non-silent error routed through `handleError` (handled ones, uncaught window
 * errors and unhandled rejections alike) to the Vinyl v3 logger's 'app' stream. `ready` gates the
 * first send on the playback environment the logger is built from. A failed send is only logged:
 * routing it back through `handleError` would report the reporter's own failure forever.
 */
export function initializeRemoteErrorReporting(ready: Promise<unknown>): Unsubscribe {
    return onError((report) => {
        ready
            .then(() => Promise.all([getVinylLogger('app'), settledDmls()]))
            .then(([logger, dmls]) => logger.log(toAppErrorLogEvent(report, dmls)))
            .catch((failure: unknown) => {
                logWarn(remoteErrorReportingTarget, 'could not report error', failure)
            })
    })
}
